import React, { useState, useEffect, useRef, useCallback } from 'react';
import { MessageSquare, Heart, Reply, X, Smile, Image, Link, List, CheckCircle, User, Star, Bookmark, Filter, Search, List as ListIcon, Bookmark as BookmarkIcon, Image as ImageIcon } from 'lucide-react';

interface Comment {
  id: string;
  content: string;
  author: {
    id: string;
    name: string;
    avatar?: string;
    role: 'author' | 'moderator' | 'member';
    isVerified: boolean;
  };
  position: {
    startOffset: number;
    endOffset: number;
    xpath: string;
    selectedText?: string;
  };
  createdAt: Date;
  updatedAt: Date;
  likes: number;
  isLiked: boolean;
  replies: Comment[];
  isEdited: boolean;
  isPinned: boolean;
  isResolved: boolean;
  tags: string[];
  mentions: string[];
  attachments: {
    id: string;
    type: 'image' | 'file' | 'link';
    url: string;
    name: string;
    size?: number;
  }[];
  reactions: {
    emoji: string;
    count: number;
    users: string[];
  }[];
  isPrivate: boolean;
  isModerated: boolean;
  moderationReason?: string;
}

interface InlineCommentsProps {
  articleId: string;
  contentRef: React.RefObject<HTMLElement | null>;
  onCommentCreate?: (comment: Comment) => void;
  onCommentUpdate?: (comment: Comment) => void;
  onCommentDelete?: (commentId: string) => void;
  onCommentReply?: (commentId: string, reply: Comment) => void;
  onCommentLike?: (commentId: string, liked: boolean) => void;
  onCommentPin?: (commentId: string, pinned: boolean) => void;
  onCommentResolve?: (commentId: string, resolved: boolean) => void;
  className?: string;
}

const InlineComments: React.FC<InlineCommentsProps> = ({
  articleId,
  contentRef,
  onCommentCreate,
  onCommentUpdate,
  onCommentDelete,
  onCommentReply,
  onCommentLike,
  onCommentPin,
  onCommentResolve,
  className = ''
}) => {
  const [comments, setComments] = useState<Comment[]>([]);
  const [selectedText, setSelectedText] = useState('');
  const [showCommentBox, setShowCommentBox] = useState(false);
  const [commentPosition, setCommentPosition] = useState({ x: 0, y: 0 });
  const [newComment, setNewComment] = useState('');
  const [replyingTo, setReplyingTo] = useState<string | null>(null);
  const [replyContent, setReplyContent] = useState('');
  const [showComments, setShowComments] = useState(true);
  const [filterType, setFilterType] = useState<'all' | 'unresolved' | 'pinned' | 'recent'>('all');
  const [sortBy, setSortBy] = useState<'recent' | 'popular' | 'oldest'>('recent');
  const [searchQuery, setSearchQuery] = useState('');
  const [showReactions, setShowReactions] = useState<string | null>(null);
  const [isModerator, setIsModerator] = useState(false);

  const commentBoxRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Mock data
  useEffect(() => {
    const mockComments: Comment[] = [
      {
        id: '1',
        content: 'This is a great explanation of React Server Components! I\'ve been wondering about the performance benefits.',
        author: {
          id: 'user1',
          name: 'Sarah Chen',
          avatar: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?w=40&h=40&fit=crop&crop=face',
          role: 'member',
          isVerified: true
        },
        position: {
          startOffset: 0,
          endOffset: 0,
          xpath: '/html/body/div[1]/p[1]',
          selectedText: 'React Server Components represent one of the most significant shifts'
        },
        createdAt: new Date('2024-01-20T10:30:00Z'),
        updatedAt: new Date('2024-01-20T10:30:00Z'),
        likes: 12,
        isLiked: false,
        replies: [
          {
            id: '1-1',
            content: 'I agree! The performance improvements are substantial.',
            author: {
              id: 'user2',
              name: 'Mike Johnson',
              avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=40&h=40&fit=crop&crop=face',
              role: 'member',
              isVerified: false
            },
            position: {
              startOffset: 0,
              endOffset: 0,
              xpath: '/html/body/div[1]/p[1]'
            },
            createdAt: new Date('2024-01-20T11:15:00Z'),
            updatedAt: new Date('2024-01-20T11:15:00Z'),
            likes: 3,
            isLiked: true,
            replies: [],
            isEdited: false,
            isPinned: false,
            isResolved: false,
            tags: [],
            mentions: [],
            attachments: [],
            reactions: [],
            isPrivate: false,
            isModerated: false
          }
        ],
        isEdited: false,
        isPinned: false,
        isResolved: false,
        tags: ['react', 'performance'],
        mentions: [],
        attachments: [],
        reactions: [
          { emoji: '👍', count: 8, users: ['user1', 'user2', 'user3'] },
          { emoji: '❤️', count: 4, users: ['user4', 'user5'] }
        ],
        isPrivate: false,
        isModerated: false
      },
      {
        id: '2',
        content: 'Has anyone tried implementing RSCs in production? What were the challenges?',
        author: {
          id: 'user3',
          name: 'Alex Rodriguez',
          avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=40&h=40&fit=crop&crop=face',
          role: 'member',
          isVerified: true
        },
        position: {
          startOffset: 0,
          endOffset: 0,
          xpath: '/html/body/div[1]/p[2]',
          selectedText: 'Unlike traditional client-side components, RSCs run on the server'
        },
        createdAt: new Date('2024-01-20T14:20:00Z'),
        updatedAt: new Date('2024-01-20T14:20:00Z'),
        likes: 7,
        isLiked: false,
        replies: [],
        isEdited: false,
        isPinned: true,
        isResolved: false,
        tags: ['question', 'production'],
        mentions: [],
        attachments: [],
        reactions: [
          { emoji: '🤔', count: 2, users: ['user6', 'user7'] }
        ],
        isPrivate: false,
        isModerated: false
      }
    ];

    setComments(mockComments);
  }, [articleId]);

  // Handle text selection
  const handleTextSelection = useCallback(() => {
    const selection = window.getSelection();
    if (selection && selection.toString().trim()) {
      const selectedText = selection.toString().trim();
      setSelectedText(selectedText);
      
      // Get selection position
      const range = selection.getRangeAt(0);
      const rect = range.getBoundingClientRect();
      setCommentPosition({
        x: rect.left + rect.width / 2,
        y: rect.top - 10
      });
      
      setShowCommentBox(true);
    }
  }, []);

  // Create comment
  const createComment = async () => {
    if (!newComment.trim()) return;

    const comment: Comment = {
      id: Date.now().toString(),
      content: newComment,
      author: {
        id: 'current-user',
        name: 'Current User',
        avatar: 'https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?w=40&h=40&fit=crop&crop=face',
        role: 'member',
        isVerified: true
      },
      position: {
        startOffset: 0,
        endOffset: 0,
        xpath: '',
        selectedText: selectedText
      },
      createdAt: new Date(),
      updatedAt: new Date(),
      likes: 0,
      isLiked: false,
      replies: [],
      isEdited: false,
      isPinned: false,
      isResolved: false,
      tags: [],
      mentions: [],
      attachments: [],
      reactions: [],
      isPrivate: false,
      isModerated: false
    };

    setComments(prev => [comment, ...prev]);
    setNewComment('');
    setShowCommentBox(false);
    onCommentCreate?.(comment);
  };

  // Create reply
  const createReply = async (parentId: string) => {
    if (!replyContent.trim()) return;

    const reply: Comment = {
      id: Date.now().toString(),
      content: replyContent,
      author: {
        id: 'current-user',
        name: 'Current User',
        avatar: 'https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?w=40&h=40&fit=crop&crop=face',
        role: 'member',
        isVerified: true
      },
      position: {
        startOffset: 0,
        endOffset: 0,
        xpath: ''
      },
      createdAt: new Date(),
      updatedAt: new Date(),
      likes: 0,
      isLiked: false,
      replies: [],
      isEdited: false,
      isPinned: false,
      isResolved: false,
      tags: [],
      mentions: [],
      attachments: [],
      reactions: [],
      isPrivate: false,
      isModerated: false
    };

    setComments(prev => prev.map(comment => 
      comment.id === parentId 
        ? { ...comment, replies: [...comment.replies, reply] }
        : comment
    ));

    setReplyContent('');
    setReplyingTo(null);
    onCommentReply?.(parentId, reply);
  };

  // Handle comment actions
  const handleLike = (commentId: string, liked: boolean) => {
    setComments(prev => prev.map(comment => 
      comment.id === commentId 
        ? { ...comment, isLiked: liked, likes: liked ? comment.likes + 1 : comment.likes - 1 }
        : comment
    ));
    onCommentLike?.(commentId, liked);
  };

  const handlePin = (commentId: string, pinned: boolean) => {
    setComments(prev => prev.map(comment => 
      comment.id === commentId 
        ? { ...comment, isPinned: pinned }
        : comment
    ));
    onCommentPin?.(commentId, pinned);
  };

  const handleResolve = (commentId: string, resolved: boolean) => {
    setComments(prev => prev.map(comment => 
      comment.id === commentId 
        ? { ...comment, isResolved: resolved }
        : comment
    ));
    onCommentResolve?.(commentId, resolved);
  };

  // Filter and sort comments
  const filteredComments = comments
    .filter(comment => {
      const matchesFilter = filterType === 'all' || 
        (filterType === 'unresolved' && !comment.isResolved) ||
        (filterType === 'pinned' && comment.isPinned) ||
        (filterType === 'recent' && new Date().getTime() - comment.createdAt.getTime() < 24 * 60 * 60 * 1000);
      
      const matchesSearch = searchQuery === '' || 
        comment.content.toLowerCase().includes(searchQuery.toLowerCase()) ||
        comment.author.name.toLowerCase().includes(searchQuery.toLowerCase());
      
      return matchesFilter && matchesSearch;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'recent':
          return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
        case 'popular':
          return b.likes - a.likes;
        case 'oldest':
          return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
        default:
          return 0;
      }
    });

  // Format date
  const formatDate = (date: Date) => {
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return date.toLocaleDateString();
  };

  // Add event listeners
  useEffect(() => {
    if (contentRef.current) {
      contentRef.current.addEventListener('mouseup', handleTextSelection);
      return () => {
        contentRef.current?.removeEventListener('mouseup', handleTextSelection);
      };
    }
  }, [contentRef, handleTextSelection]);

  return (
    <div className={`inline-comments ${className}`}>
      {/* Comment Box */}
      {showCommentBox && (
        <div
          ref={commentBoxRef}
          className="fixed z-50 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 p-4 max-w-md"
          style={{
            left: Math.min(commentPosition.x - 200, window.innerWidth - 400),
            top: Math.max(commentPosition.y - 100, 10)
          }}
        >
          <div className="flex items-start gap-3">
            <img
              src="https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?w=40&h=40&fit=crop&crop=face"
              alt="User"
              className="w-8 h-8 rounded-full"
            />
            <div className="flex-1">
              <div className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                Commenting on: "{selectedText}"
              </div>
              <textarea
                ref={textareaRef}
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                placeholder="Add a comment..."
                className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white resize-none"
                rows={3}
                autoFocus
              />
              <div className="flex items-center justify-between mt-3">
                <div className="flex items-center gap-2">
                  <button className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors">
                    <Smile className="w-4 h-4" />
                  </button>
                  <button className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors">
                    <Image className="w-4 h-4" />
                  </button>
                  <button className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors">
                    <Link className="w-4 h-4" />
                  </button>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setShowCommentBox(false)}
                    className="px-3 py-1 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={createComment}
                    disabled={!newComment.trim()}
                    className="px-4 py-1 bg-blue-500 hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded transition-colors"
                  >
                    Comment
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Comments Panel */}
      {showComments && (
        <div className="fixed right-0 top-0 h-full w-96 bg-white dark:bg-gray-800 border-l border-gray-200 dark:border-gray-700 shadow-lg z-40">
          <div className="p-4 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                <MessageSquare className="w-5 h-5 text-blue-500" />
                Comments ({filteredComments.length})
              </h3>
              <button
                onClick={() => setShowComments(false)}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            
            {/* Filters */}
            <div className="flex gap-2 mb-4">
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value as any)}
                className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
              >
                <option value="all">All Comments</option>
                <option value="unresolved">Unresolved</option>
                <option value="pinned">Pinned</option>
                <option value="recent">Recent</option>
              </select>
              
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as any)}
                className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
              >
                <option value="recent">Recent</option>
                <option value="popular">Popular</option>
                <option value="oldest">Oldest</option>
              </select>
            </div>
            
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search comments..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
              />
            </div>
          </div>
          
          <div className="flex-1 overflow-y-auto p-4">
            <div className="space-y-4">
              {filteredComments.map(comment => (
                <div
                  key={comment.id}
                  className={`p-4 border rounded-lg transition-colors ${
                    comment.isPinned 
                      ? 'border-yellow-200 bg-yellow-50 dark:border-yellow-800 dark:bg-yellow-900/20' 
                      : 'border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <img
                      src={comment.author.avatar}
                      alt={comment.author.name}
                      className="w-8 h-8 rounded-full"
                    />
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium text-gray-900 dark:text-white text-sm">
                          {comment.author.name}
                        </span>
                        {comment.author.isVerified && (
                          <CheckCircle className="w-4 h-4 text-blue-500" />
                        )}
                        <span className="text-xs text-gray-500">
                          {formatDate(comment.createdAt)}
                        </span>
                        {comment.isPinned && (
                          <span className="text-xs px-2 py-1 bg-yellow-100 dark:bg-yellow-900 text-yellow-600 dark:text-yellow-400 rounded-full">
                            Pinned
                          </span>
                        )}
                        {comment.isResolved && (
                          <span className="text-xs px-2 py-1 bg-green-100 dark:bg-green-900 text-green-600 dark:text-green-400 rounded-full">
                            Resolved
                          </span>
                        )}
                      </div>
                      
                      <p className="text-gray-900 dark:text-white text-sm mb-3">
                        {comment.content}
                      </p>
                      
                      {/* Selected Text */}
                      {comment.position.selectedText && (
                        <div className="bg-gray-100 dark:bg-gray-700 p-2 rounded text-xs text-gray-600 dark:text-gray-400 mb-3">
                          "{comment.position.selectedText}"
                        </div>
                      )}
                      
                      {/* Reactions */}
                      {comment.reactions.length > 0 && (
                        <div className="flex items-center gap-2 mb-3">
                          {comment.reactions.map((reaction, index) => (
                            <button
                              key={index}
                              className="flex items-center gap-1 px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded-full text-xs hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                            >
                              <span>{reaction.emoji}</span>
                              <span>{reaction.count}</span>
                            </button>
                          ))}
                        </div>
                      )}
                      
                      {/* Actions */}
                      <div className="flex items-center gap-4 text-xs text-gray-500">
                        <button
                          onClick={() => handleLike(comment.id, !comment.isLiked)}
                          className={`flex items-center gap-1 hover:text-red-500 transition-colors ${
                            comment.isLiked ? 'text-red-500' : ''
                          }`}
                        >
                          <Heart className={`w-4 h-4 ${comment.isLiked ? 'fill-current' : ''}`} />
                          <span>{comment.likes}</span>
                        </button>
                        
                        <button
                          onClick={() => setReplyingTo(replyingTo === comment.id ? null : comment.id)}
                          className="flex items-center gap-1 hover:text-blue-500 transition-colors"
                        >
                          <Reply className="w-4 h-4" />
                          <span>Reply</span>
                        </button>
                        
                        {isModerator && (
                          <>
                            <button
                              onClick={() => handlePin(comment.id, !comment.isPinned)}
                              className="flex items-center gap-1 hover:text-yellow-500 transition-colors"
                            >
                              <Star className={`w-4 h-4 ${comment.isPinned ? 'fill-current text-yellow-500' : ''}`} />
                              <span>Pin</span>
                            </button>
                            
                            <button
                              onClick={() => handleResolve(comment.id, !comment.isResolved)}
                              className="flex items-center gap-1 hover:text-green-500 transition-colors"
                            >
                              <CheckCircle className={`w-4 h-4 ${comment.isResolved ? 'text-green-500' : ''}`} />
                              <span>Resolve</span>
                            </button>
                          </>
                        )}
                      </div>
                      
                      {/* Reply Form */}
                      {replyingTo === comment.id && (
                        <div className="mt-3 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                          <div className="flex items-start gap-2">
                            <img
                              src="https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?w=32&h=32&fit=crop&crop=face"
                              alt="User"
                              className="w-6 h-6 rounded-full"
                            />
                            <div className="flex-1">
                              <textarea
                                value={replyContent}
                                onChange={(e) => setReplyContent(e.target.value)}
                                placeholder="Write a reply..."
                                className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm resize-none"
                                rows={2}
                              />
                              <div className="flex items-center justify-end gap-2 mt-2">
                                <button
                                  onClick={() => setReplyingTo(null)}
                                  className="px-3 py-1 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-600 rounded text-sm transition-colors"
                                >
                                  Cancel
                                </button>
                                <button
                                  onClick={() => createReply(comment.id)}
                                  disabled={!replyContent.trim()}
                                  className="px-3 py-1 bg-blue-500 hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded text-sm transition-colors"
                                >
                                  Reply
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                      
                      {/* Replies */}
                      {comment.replies.length > 0 && (
                        <div className="mt-3 space-y-3">
                          {comment.replies.map(reply => (
                            <div key={reply.id} className="flex items-start gap-2 pl-4 border-l-2 border-gray-200 dark:border-gray-600">
                              <img
                                src={reply.author.avatar}
                                alt={reply.author.name}
                                className="w-6 h-6 rounded-full"
                              />
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-1">
                                  <span className="font-medium text-gray-900 dark:text-white text-xs">
                                    {reply.author.name}
                                  </span>
                                  <span className="text-xs text-gray-500">
                                    {formatDate(reply.createdAt)}
                                  </span>
                                </div>
                                <p className="text-gray-900 dark:text-white text-xs">
                                  {reply.content}
                                </p>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
              
              {filteredComments.length === 0 && (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                  <MessageSquare className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>No comments found</p>
                  <p className="text-sm">Select text to add a comment</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InlineComments;
